/**
 * @file     useLandingAttribution.ts
 * @purpose  Reads landing_slug + landing_variant + landing_intent from the current URL, persisting to sessionStorage so the values survive step-to-step navigation inside the funnel and are available at lead submission time.
 */
'use client';

import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

const SESSION_KEY = 'relta_landing_attribution';
/**
 * Time-to-live for stored attribution. Past this, the session entry is treated
 * as expired so a user who opens /l/[slug] in one tab and later comes back to
 * a generic funnel page in the same tab does not get falsely tagged.
 * 30 minutes covers normal landing-to-funnel completion comfortably.
 */
const TTL_MS = 30 * 60 * 1000;

export type LandingAttribution = {
  slug?: string;
  variant?: string;
  intent?: string;
  /**
   * BCP-47 locale code (e.g. 'fr', 'es', 'ar') the page was viewed in.
   * Captured at landing-page render time from the i18n provider so the CRM
   * knows which language the user saw the offer in — even if the lead is
   * submitted later. Backend enum values and field NAMES stay English.
   */
  pageLocale?: string;
};

type StoredAttribution = LandingAttribution & { capturedAt: number };

const readStored = (): LandingAttribution => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredAttribution;
    if (typeof parsed.capturedAt !== 'number' || Date.now() - parsed.capturedAt > TTL_MS) {
      sessionStorage.removeItem(SESSION_KEY);
      return {};
    }
    return {
      slug: parsed.slug,
      variant: parsed.variant,
      intent: parsed.intent,
      pageLocale: parsed.pageLocale,
    };
  } catch {
    return {};
  }
};

export const useLandingAttribution = (): LandingAttribution => {
  const searchParams = useSearchParams();
  const { i18n } = useTranslation();
  // BCP-47 code from the i18n provider. Falls back to the documentElement
  // lang attribute (set by ThemeBootstrap) so the value is meaningful even
  // before i18n has hydrated, then to 'en' as a final guard.
  const pageLocale =
    (typeof i18n.language === 'string' && i18n.language) ||
    (typeof document !== 'undefined' && document.documentElement.lang) ||
    'en';

  // Capture from URL on first render and persist. Subsequent renders read from session.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const slug = searchParams?.get('landing_slug') ?? undefined;
    const variant = searchParams?.get('landing_variant') ?? undefined;
    const intent = searchParams?.get('landing_intent') ?? undefined;
    const locale = searchParams?.get('landing_locale') ?? pageLocale;
    if (slug || variant || intent) {
      try {
        const payload: StoredAttribution = {
          slug,
          variant,
          intent,
          pageLocale: locale,
          capturedAt: Date.now(),
        };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
      } catch {
        // sessionStorage may be disabled — non-fatal.
      }
    }
  }, [searchParams, pageLocale]);

  return useMemo<LandingAttribution>(() => {
    const fromUrl = {
      slug: searchParams?.get('landing_slug') ?? undefined,
      variant: searchParams?.get('landing_variant') ?? undefined,
      intent: searchParams?.get('landing_intent') ?? undefined,
      pageLocale: searchParams?.get('landing_locale') ?? pageLocale,
    };
    if (fromUrl.slug || fromUrl.variant) return fromUrl;
    const stored = readStored();
    // Prefer the live locale (user may have switched language since capture).
    return { ...stored, pageLocale: stored.pageLocale ?? pageLocale };
  }, [searchParams, pageLocale]);
};
